import { useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { CustomerPicker, LoadingOverlay, RECEIVABLES_API, fmtDate, fmtDateTime, money, useReceivableLookups } from "./common";

const ACTION_BADGE = {
  INVOICE_CREATED: "badge-blue",
  DELIVERY_ASSIGNED: "badge-blue",
  DELIVERY_UNASSIGNED: "badge-gray",
  SHEET_PRINTED: "badge-gray",
  PAYMENT: "badge-green",
  CREDIT_NOTE: "badge-amber",
  RETURN: "badge-red",
  CANCELLATION: "badge-red",
  ADJUSTMENT: "badge-amber",
  VISIT: "badge-gray",
};

/**
 * Payment / bill number history: everything done on a bill — created, assigned for delivery, loading / trip / unloading sheet
 * printed (and by whom), payments received by the delivery boy or the salesman, credit notes, returns and adjustments.
 */
const BillHistory = () => {
  const { salesmen, beats } = useReceivableLookups(["salesmen", "beats"]);
  const [billNumber, setBillNumber] = useState("");
  const [salesmanUserId, setSalesmanUserId] = useState("");
  const [beatId, setBeatId] = useState("");
  const [customer, setCustomer] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const params = () => ({
    billNumber: billNumber.trim() || undefined,
    salesmanUserId: salesmanUserId || undefined,
    beatId: beatId || undefined,
    customerId: customer?.id,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
  });

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get(`${RECEIVABLES_API}/bill-history`, params());
      setRows(Array.isArray(data) ? data : []);
      setLoaded(true);
    } catch (error) {
      toast.error(error.message || "Failed to load the bill history");
    } finally {
      setLoading(false);
    }
  };

  const download = async (kind) => {
    try {
      const suffix = billNumber.trim() || new Date().toISOString().slice(0, 10);
      await api.downloadFile(`${RECEIVABLES_API}/bill-history/${kind === "pdf" ? "pdf" : "export"}`, params(), `bill-history-${suffix}.${kind === "pdf" ? "pdf" : "xlsx"}`);
    } catch (error) {
      toast.error(error.message || "Download failed");
    }
  };

  const beatOptions = salesmanUserId ? beats.filter((b) => String(b.salesmanUserId) === String(salesmanUserId)) : beats;

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="page-subtitle" style={{ marginBottom: 12 }}>
        Complete history of a bill: delivery assignment, who printed the loading / trip / unloading sheet, payments received by the
        delivery boy and by the salesman, credit notes, returns and adjustments. With a bill number its whole history is shown; otherwise
        the actions in the date range (last 30 days by default).
      </div>
      <div className="filters-bar">
        <input value={billNumber} onChange={(e) => setBillNumber(e.target.value)} placeholder="Bill number" style={{ width: 150 }} />
        <select
          value={salesmanUserId}
          onChange={(e) => {
            setSalesmanUserId(e.target.value);
            setBeatId("");
            setCustomer(null);
          }}
          aria-label="Salesman"
        >
          <option value="">All salesmen</option>
          {salesmen.map((s) => (
            <option key={s.userId} value={s.userId}>
              {s.name}
            </option>
          ))}
        </select>
        <select
          value={beatId}
          onChange={(e) => {
            setBeatId(e.target.value);
            setCustomer(null);
          }}
          aria-label="Beat"
        >
          <option value="">All beats</option>
          {beatOptions.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <CustomerPicker value={customer} onChange={setCustomer} beatId={beatId || undefined} salesmanUserId={salesmanUserId || undefined} placeholder="All customers (code / name)" />
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
        <button className="primary" onClick={load} disabled={loading}>
          Show
        </button>
        <button className="secondary" onClick={() => download("xlsx")} disabled={!rows.length}>
          Export Excel
        </button>
        <button className="secondary" onClick={() => download("pdf")} disabled={!rows.length}>
          Export PDF
        </button>
      </div>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Bill No</th>
              <th>Bill Date</th>
              <th>Customer</th>
              <th>Salesman</th>
              <th>Beat</th>
              <th>Action</th>
              <th>Action Date</th>
              <th style={{ textAlign: "right" }}>Amount</th>
              <th>Performed By</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {!loaded ? (
              <tr>
                <td colSpan={10}>
                  <div className="empty-state">
                    <span className="empty-state-title">Enter a bill number or pick filters and click Show</span>
                  </div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={10}>
                  <div className="empty-state">
                    <span className="empty-state-title">No actions found</span>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={`${row.invoiceId}-${index}`}>
                  <td>{row.billNumber}</td>
                  <td>{fmtDate(row.invoiceDate)}</td>
                  <td>{row.customerName}</td>
                  <td>{row.salesmanName || "-"}</td>
                  <td>{row.beatName || "-"}</td>
                  <td>
                    <span className={`badge ${ACTION_BADGE[row.actionType] || "badge-gray"}`}>{row.actionLabel}</span>
                  </td>
                  <td>{fmtDateTime(row.actionAt)}</td>
                  <td style={{ textAlign: "right" }}>{row.amount === null || row.amount === undefined ? "-" : money(row.amount)}</td>
                  <td>{row.performedBy || "-"}</td>
                  <td style={{ whiteSpace: "normal", maxWidth: 320 }}>{row.details || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BillHistory;
